/**
 * Turnarounds, in A-CDM milestones.
 *
 * The board is ordered by trouble rather than by time, because a duty manager
 * reads it to find what is going wrong, not to admire the schedule.
 *
 * The one thing it will not do is revise a commitment. A TOBT that moves
 * whenever it is about to be missed is not a commitment, and the sequence
 * downstream — start-up approvals, the departure order — is built on the word
 * given. So the board reports three things about it and changes none: whether
 * it could be met when it was made, whether it has passed, and whether it was
 * met.
 */
import { useMemo, useState } from "react";
import { useSession } from "../../app/SessionContext";
import { bestKnown, flightName } from "../../domain/flights";
import { checkTurnaround, derivedTobt, sortByTrouble, turnaroundsOf, TURNAROUND_LABEL } from "../../domain/turnaround";
import { Button, Card, EmptyState, Table } from "../../design/components";
import { TobtForm } from "./FlightEntryForms";
import { Page } from "../../layout/AppShell";
import { DayNotes, DayState, FlightTheme, hasDay, LiveStrip } from "./FlightTheme";
import { useScreenContext } from "../../assistant/screen";
import { Tiles } from "../executive/shared";
import { useFlightWorld } from "./useFlightWorld";
import { t, tn } from "../../i18n";
import s from "./Flights.module.css";
import { hhmm } from "../../lib/format";
import { localDate } from "../../domain/flightEntries";

export function TurnaroundsPage() {
  const session = useSession();
  const world = useFlightWorld();
  const now = new Date();
  /* the turnaround whose committed off-block time is being entered */
  const [entering, setEntering] = useState<string | null>(null);

  const turns = useMemo(() => turnaroundsOf(world.flights, world.register), [world.flights, world.register]);
  const checked = useMemo(() => turns.map((turn) => ({ turn, check: checkTurnaround(turn, now) })), [turns]);
  const ordered = useMemo(() => sortByTrouble(checked).map((i) => checked[i]), [checked]);

  const atRisk = checked.filter((x) => x.check.state === "at_risk");
  const missed = checked.filter((x) => x.check.state === "commitment_missed");
  const complete = checked.filter((x) => x.check.state === "complete");
  const kept = complete.filter((x) => (x.check.missedBy ?? 0) <= 0);
  const derived = checked.filter((x) => derivedTobt(x.turn).derived).length;

  const source = [{ kind: "entered" as const, ref: "flights", label: t("the day's schedule") }];
  const tiles = [
    {
      key: "turns",
      label: t("turnarounds today"),
      value: checked.length,
      unit: "",
      text: t("{n} away already", { n: complete.length }),
      tone: "good" as const,
      assumed: true,
      sources: source,
    },
    {
      key: "atrisk",
      label: t("cannot make their commitment"),
      value: atRisk.length,
      unit: "",
      text: atRisk.length
        ? t("on stand too late for the minimum turnaround")
        : t("every aircraft on stand has time in hand"),
      tone: atRisk.length ? ("bad" as const) : ("good" as const),
      assumed: true,
      sources: source,
    },
    {
      key: "missed",
      label: t("committed time already passed"),
      value: missed.length,
      unit: "",
      text: missed.length ? t("Still on stand past the committed time") : t("none has passed"),
      tone: missed.length ? ("warn" as const) : ("good" as const),
      assumed: true,
      sources: source,
    },
    {
      key: "kept",
      label: t("commitments kept"),
      value: complete.length ? Math.round((kept.length / complete.length) * 100) : null,
      unit: "%",
      text: complete.length
        ? t("of {n} that have pushed back", { n: complete.length })
        : t("nothing has pushed back yet"),
      assumed: true,
      sources: source,
    },
  ];

  const board = [
    t("{site}: {n} on the day.", {
      site: session.site.code,
      n: tn(checked.length, "{n} turnaround", "{n} turnarounds"),
    }),
  ];
  if (atRisk.length) {
    board.push(
      tn(
        atRisk.length,
        "{n} cannot make the off-block time committed to.",
        "{n} cannot make the off-block times committed to.",
      ),
    );
  }
  if (missed.length) {
    board.push(
      tn(
        missed.length,
        "{n} is still on stand past its committed time.",
        "{n} are still on stand past their committed time.",
      ),
    );
  }

  useScreenContext({
    route: "/turnarounds",
    title: "Turnarounds",
    worksInFull: true,
    summary: board,
    figures: new Set(
      [checked.length, atRisk.length, missed.length, complete.length]
        .map(String)
        .concat(Array.from({ length: 13 }, (_, i) => String(i))),
    ),
    own: (q) => {
      const text = q.toLowerCase();
      if (/risk|cannot|late|slip/.test(text)) {
        return atRisk.length
          ? atRisk.map((x) => x.check.says).join("\n")
          : t("Every aircraft on stand has time in hand.");
      }
      if (/commit|tobt|passed/.test(text)) {
        return missed.length
          ? missed.map((x) => x.check.says).join("\n")
          : t("No committed off-block time has passed.");
      }
      return null;
    },
    offers: [
      { label: "What cannot make it?", ask: "which turnarounds cannot make their commitment?" },
      { label: "Whose time has passed?", ask: "which committed times have passed?" },
    ],
  });

  const lede = (
    <p className={s.lede}>
      {t(
        "Ground time between each arrival and its departure, checked against the handler's committed off-block time.",
      )}
    </p>
  );
  if (!hasDay(world))
    return (
      <FlightTheme>
        <Page eyebrow={t("{site} · Flight operations", { site: session.site.code })} title="Turnarounds">
          {lede}
          <DayState world={world} />
        </Page>
      </FlightTheme>
    );

  return (
    <FlightTheme>
      <Page eyebrow={t("{site} · Flight operations", { site: session.site.code })} title="Turnarounds">
        <LiveStrip flights={world.flights} site={session.site.code} />
        {lede}
        <DayNotes world={world} />
        <p className={s.note}>
          {derived > 0
            ? t(
                "{n} of these have no committed time recorded, so the scheduled off-block time is shown instead and marked as derived. A time nobody committed to is not a commitment, and the board says which is which.",
                { n: derived },
              )
            : t("Every turnaround here carries a committed off-block time somebody actually gave.")}
        </p>

        <Tiles items={tiles} currency="SAR" />

        <Card collapsible title="The turnarounds" sub="Worst first">
          {ordered.length === 0 ? (
            <EmptyState>{t("No movement on the day is paired into a turnaround.")}</EmptyState>
          ) : (
            <Table>
              <thead>
                <tr>
                  <th>{t("Turnaround")}</th>
                  <th>{t("Stand")}</th>
                  <th className={s.num}>{t("On stand")}</th>
                  <th className={s.num}>{t("Committed off-block")}</th>
                  <th className={s.num}>{t("Off blocks")}</th>
                  <th className={s.num}>{t("Ground time")}</th>
                  <th>{t("State")}</th>
                </tr>
              </thead>
              <tbody data-testid="turnarounds-board">
                {ordered.map(({ turn, check }) => {
                  const tobt = derivedTobt(turn);
                  return (
                    <tr key={turn.ref} data-testid={`turn-${turn.ref}`}>
                      <td>
                        <b>{turn.arrival ? flightName(turn.arrival) : "—"}</b>
                        <span className={s.why}> → </span>
                        <b>{turn.departure ? flightName(turn.departure) : "—"}</b>
                        {/* the pair's key is the record's id now, not a name: show the aircraft that turns */}
                        {(turn.arrival?.registration ?? turn.departure?.registration) && (
                          <div className={s.why}>{turn.arrival?.registration ?? turn.departure?.registration}</div>
                        )}
                      </td>
                      <td className={s.mono}>{turn.standCode ?? <span className={s.unknown}>—</span>}</td>
                      <td className={s.num}>{hhmm(turn.aibt)}</td>
                      <td className={s.num}>
                        {hhmm(tobt.at)}
                        {tobt.derived && <div className={s.why}>{t("derived")}</div>}
                        {!tobt.derived && turn.departure?.tobtBy && (
                          <div className={s.why}>{t("by {who}", { who: turn.departure.tobtBy })}</div>
                        )}
                        {turn.departure && !turn.aobt && !turn.departure.cancelled && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEntering(entering === turn.ref ? null : turn.ref)}
                            data-testid={`tobt-enter-${turn.ref}`}
                          >
                            {tobt.derived ? t("Enter") : t("Change")}
                          </Button>
                        )}
                      </td>
                      <td className={s.num}>
                        {turn.aobt ? <b>{hhmm(turn.aobt)}</b> : <span className={s.unknown}>{t("not yet")}</span>}
                      </td>
                      <td className={s.num}>
                        {check.groundMinutes === null ? (
                          <span className={s.unknown}>—</span>
                        ) : (
                          <>
                            {check.groundMinutes}
                            {check.minimum !== null && (
                              <div className={check.groundMinutes < check.minimum ? s.bad : s.why}>
                                {t("min {n}", { n: check.minimum })}
                              </div>
                            )}
                          </>
                        )}
                      </td>
                      <td>
                        <span className={s.state} data-state={check.state}>
                          {t(TURNAROUND_LABEL[check.state])}
                        </span>
                        {check.missedBy !== null && check.missedBy > 0 && (
                          <div className={s.why}>{t("{n} late off blocks", { n: check.missedBy })}</div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card>

        {(() => {
          const turn = entering ? turns.find((x) => x.ref === entering) : undefined;
          const dep = turn?.departure;
          if (!turn || !dep) return null;
          return (
            <Card
              title="Committed off-block time"
              sub={t("{f}, as the handler gave it", { f: flightName(dep) })}
              testId="turnaround-tobt"
              actions={
                <Button size="sm" variant="ghost" onClick={() => setEntering(null)}>
                  {t("Close")}
                </Button>
              }
            >
              <p className={s.note}>
                {dep.tobt
                  ? t("Committed now: {t}. A new time replaces it; the record keeps both.", { t: hhmm(dep.tobt) })
                  : t("No committed time yet. Scheduled off-block is {t}.", { t: hhmm(dep.scheduledAt) })}
              </p>
              <TobtForm
                key={turn.ref}
                movementId={dep.id}
                serviceDate={serviceDateOf(dep)}
                near={dep.scheduledAt}
                onDone={() => setEntering(null)}
              />
            </Card>
          );
        })()}

        {(atRisk.length > 0 || missed.length > 0) && (
          <Card
            collapsible
            title="What will not make it"
            sub="In full"
          >
            <ul className={s.conflicts} data-testid="turnaround-trouble">
              {[...atRisk, ...missed].map(({ turn, check }) => (
                <li
                  key={turn.ref}
                  className={s.conflict}
                  data-kind={check.state === "at_risk" ? "occupied" : "blocked"}
                >
                  <span className={s.standCode}>
                    {turn.standCode ?? (turn.arrival ? flightName(turn.arrival) : turn.departure ? flightName(turn.departure) : "—")}
                  </span>
                  <span>{check.says}</span>
                </li>
              ))}
            </ul>
          </Card>
        )}

      </Page>
    </FlightTheme>
  );
}

/** The flight's day in the airport's time, which the platform checks a committed time against. */
function serviceDateOf(f: { scheduledAt?: string }): string {
  return localDate(f.scheduledAt ?? Date.now());
}

export { bestKnown };
